import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  TextField,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { checkoutApi, couponsApi, publicPaymentApi, type PaymentProduct, type PaymentPrice, type CheckoutResult, type ValidatedCoupon } from '../../api';
import { useCredits } from '../../hooks';
import {
  trackBeginCheckout,
  trackAddPaymentInfo,
  getGA4ClientId,
  type CreditPack,
} from '../../lib/analytics';

interface CheckoutModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  product: PaymentProduct;
  price: PaymentPrice;
}

// Payment form component - inside Elements provider
const PaymentForm: React.FC<{
  onSuccess: () => void;
  onError: (error: string) => void;
  checkoutResult: CheckoutResult;
}> = ({ onSuccess, onError, checkoutResult }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const { refreshCredits } = useCredits();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/credits?success=true`,
        },
        redirect: 'if_required',
      });

      if (error) {
        onError(error.message || 'Payment failed');
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Confirm with our backend
        await checkoutApi.confirmPayment({ paymentIntentId: paymentIntent.id });
        await refreshCredits();
        onSuccess();
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Payment failed');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ mb: 3 }}>
        <PaymentElement />
      </Box>
      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        disabled={!stripe || isProcessing}
      >
        {isProcessing ? (
          <>
            <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
            Processing...
          </>
        ) : (
          `Pay $${(checkoutResult.amount / 100).toFixed(2)}`
        )}
      </Button>
    </form>
  );
};

// Helper to calculate discount preview
function calculateDiscountPreview(coupon: ValidatedCoupon, originalAmount: number): number {
  if (coupon.type === 'percent' && coupon.percentOff) {
    return Math.round(originalAmount * (coupon.percentOff / 100));
  } else if (coupon.type === 'fixed' && coupon.amountOff) {
    return Math.min(coupon.amountOff, originalAmount - 50); // Leave at least 50 cents
  }
  return 0;
}

export const CheckoutModal: React.FC<CheckoutModalProps> = ({
  open,
  onClose,
  onSuccess: onSuccessCallback,
  product,
  price,
}) => {
  const { refreshCredits } = useCredits();
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [checkoutResult, setCheckoutResult] = useState<CheckoutResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState<string | null>(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState<ValidatedCoupon | null>(null);

  // Track if we've already fired analytics events for this checkout session
  const hasTrackedBeginCheckout = useRef(false);
  const hasTrackedPaymentInfo = useRef(false);

  // Convert product to CreditPack format for analytics
  const toCreditPack = useCallback((): CreditPack => ({
    id: product.id,
    lookupKey: product.lookupKey,
    name: product.name,
    description: product.description,
    credits: product.metadata.boardCredits || 0,
    price: price.amount,
    currency: price.currency,
  }), [product, price]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setStripe(null);
      setCheckoutResult(null);
      setError(null);
      setSuccess(false);
      setCouponCode('');
      setCouponError(null);
      setAppliedCoupon(null);
      // Reset analytics tracking refs
      hasTrackedBeginCheckout.current = false;
      hasTrackedPaymentInfo.current = false;
    }
  }, [open]);

  // Initialize checkout (load Stripe config)
  const initCheckout = useCallback(async (coupon?: string) => {
    setLoading(true);
    setError(null);

    try {
      // Track begin_checkout event (only once per modal open)
      if (!hasTrackedBeginCheckout.current) {
        trackBeginCheckout(toCreditPack(), coupon);
        hasTrackedBeginCheckout.current = true;
      }

      // Get GA4 client_id for backend attribution
      const gaClientId = await getGA4ClientId();

      // Create checkout session first (with coupon if applied)
      const result = await checkoutApi.createCheckout({
        priceId: price.id,
        couponCode: coupon,
        gaClientId: gaClientId || undefined,
      });
      setCheckoutResult(result);

      // If it's a free checkout (100% off coupon), we're done - credits already granted
      if (result.free) {
        // Credits were already granted, mark as success
        await refreshCredits();
        setSuccess(true);
        onSuccessCallback?.();
        return;
      }

      // Only load Stripe if we need to process a payment
      const config = await publicPaymentApi.getPublicConfig();
      if (!config.publishableKey) {
        throw new Error('Payment is not configured');
      }
      // Await the load rather than handing the pending promise to <Elements>:
      // js.stripe.com is blocked often enough (ad blockers, CSP, flaky
      // networks) that an unawaited rejection would escape as an unhandled
      // promise and leave the modal sitting with no payment form and no
      // explanation of why.
      const stripeInstance = await loadStripe(config.publishableKey).catch((loadError) => {
        console.error('Failed to load Stripe.js:', loadError);
        return null;
      });
      if (!stripeInstance) {
        throw new Error(
          'We could not load the secure payment form. Disable any ad blocker for this site and try again.',
        );
      }
      setStripe(stripeInstance);

      // Track add_payment_info event when Stripe form will be shown
      if (!hasTrackedPaymentInfo.current) {
        trackAddPaymentInfo(toCreditPack(), coupon);
        hasTrackedPaymentInfo.current = true;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initialize checkout');
    } finally {
      setLoading(false);
    }
  }, [price.id, refreshCredits, onSuccessCallback, toCreditPack]);

  // Start checkout when modal opens
  useEffect(() => {
    if (open) {
      initCheckout();
    }
  }, [open, initCheckout]);

  // Handle coupon validation and application
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      return;
    }

    setValidatingCoupon(true);
    setCouponError(null);

    try {
      const result = await couponsApi.validateCoupon({
        code: couponCode.trim(),
        productId: product.id,
      });

      if (result.valid && result.coupon) {
        setAppliedCoupon(result.coupon);
        // Re-create checkout with coupon
        await initCheckout(result.coupon.code);
      } else {
        setCouponError(result.error || 'Invalid coupon code');
      }
    } catch (err) {
      setCouponError(err instanceof Error ? err.message : 'Failed to validate coupon');
    } finally {
      setValidatingCoupon(false);
    }
  };

  // Handle removing coupon
  const handleRemoveCoupon = async () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError(null);
    // Re-create checkout without coupon
    await initCheckout();
  };

  const handleSuccess = () => {
    setSuccess(true);
    onSuccessCallback?.();
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const creditsToReceive = product.metadata.boardCredits || 0;
  const originalAmount = price.amount;
  const discountAmount = appliedCoupon ? calculateDiscountPreview(appliedCoupon, originalAmount) : 0;
  const finalAmount = checkoutResult?.amount || (originalAmount - discountAmount);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {success ? 'Payment Successful!' : `Purchase ${product.name}`}
      </DialogTitle>
      <DialogContent>
        {success ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            You've successfully purchased {creditsToReceive} credit{creditsToReceive !== 1 ? 's' : ''}!
            You can now create new boards.
          </Alert>
        ) : (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                {product.description}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                <Typography variant="h6">
                  {creditsToReceive} Credit{creditsToReceive !== 1 ? 's' : ''}
                </Typography>
                <Box sx={{ textAlign: 'right' }}>
                  {discountAmount > 0 ? (
                    <>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ textDecoration: 'line-through' }}
                      >
                        ${(originalAmount / 100).toFixed(2)}
                      </Typography>
                      <Typography variant="h5" color="success.main">
                        ${(finalAmount / 100).toFixed(2)}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="h5" color="primary">
                      ${(originalAmount / 100).toFixed(2)}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Coupon Section */}
            <Box sx={{ mb: 2 }}>
              {appliedCoupon ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={`${appliedCoupon.code} - ${appliedCoupon.type === 'percent' ? `${appliedCoupon.percentOff}% off` : `$${((appliedCoupon.amountOff || 0) / 100).toFixed(2)} off`}`}
                    color="success"
                    onDelete={handleRemoveCoupon}
                    sx={{ flex: 1 }}
                  />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Coupon code"
                    value={couponCode}
                    onChange={(e) => {
                      setCouponCode(e.target.value.toUpperCase());
                      setCouponError(null);
                    }}
                    error={!!couponError}
                    helperText={couponError}
                    disabled={validatingCoupon || loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocalOfferIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ flex: 1 }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleApplyCoupon();
                      }
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleApplyCoupon}
                    disabled={!couponCode.trim() || validatingCoupon || loading}
                  >
                    {validatingCoupon ? <CircularProgress size={20} /> : 'Apply'}
                  </Button>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : stripe && checkoutResult && checkoutResult.clientSecret ? (
              <Elements
                stripe={stripe}
                options={{
                  clientSecret: checkoutResult.clientSecret,
                  appearance: {
                    theme: 'stripe',
                  },
                }}
              >
                <PaymentForm
                  onSuccess={handleSuccess}
                  onError={handleError}
                  checkoutResult={checkoutResult}
                />
              </Elements>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {success ? (
          <Button onClick={handleClose} variant="contained">
            Done
          </Button>
        ) : (
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
